import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, LogOut, ShoppingCart, Ticket } from "lucide-react";
import { useApp } from "@/context/AppContext";

export const Navbar = () => {
  const { t, lang, toggleLang, user, login, logout, pkg } = useApp();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/tours", label: t.nav.tours },
    { to: "/calendario", label: t.nav.calendar },
    { to: "/fin-de-semana", label: t.nav.weekend },
    { to: "/entre-semana", label: t.nav.weekdays },
    { to: "/principiantes", label: t.nav.beginners },
    { to: "/blog", label: t.nav.blog },
  ];

  return (
    <header className="absolute top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 text-white drop-shadow">
          <img src="/logo-zenda.png" alt="Zenda Hiking" className="h-11 w-11 object-contain" />
          <span className="font-heading text-lg tracking-[0.18em] uppercase">Zenda Hiking</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 rounded-full bg-white/12 backdrop-blur-xl border border-white/25 px-2 py-1.5">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={`px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                loc.pathname === l.to ? "bg-white text-[#16221D]" : "text-white/90 hover:bg-white/20"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleLang}
            data-testid="lang-toggle"
            aria-label={lang === "es" ? "Cambiar a inglés" : "Switch to Spanish"}
            className="flex items-center gap-1.5 rounded-full border border-white/40 bg-white/15 backdrop-blur-md text-white text-xs sm:text-sm font-semibold px-2.5 sm:px-3 py-2 hover:bg-white/25 transition-colors duration-300"
          >
            <span aria-hidden>{lang === "es" ? "🇲🇽" : "🇺🇸"}</span>
            <span>{lang === "es" ? "ES" : "EN"}</span>
            <span className="text-white/50">|</span>
            <span className="text-white/70">{lang === "es" ? "EN" : "ES"}</span>
          </button>

          <Link to="/gestionar-reserva" data-testid="nav-manage"
            aria-label={lang === "es" ? "Gestionar mi reserva" : "Manage my booking"}
            title={lang === "es" ? "Gestionar mi reserva" : "Manage my booking"}
            className="hidden lg:flex items-center rounded-full border border-white/40 bg-white/15 backdrop-blur-md text-white p-2.5 hover:bg-white/25 transition-colors duration-300">
            <Ticket className="h-[18px] w-[18px]" />
          </Link>

          <Link to="/paquete" data-testid="nav-cart"
            aria-label={lang === "es" ? "Mi carrito de salidas" : "My trip cart"}
            className="relative flex items-center rounded-full border border-white/40 bg-white/15 backdrop-blur-md text-white p-2 sm:p-2.5 hover:bg-white/25 transition-colors duration-300">
            <ShoppingCart className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
            {pkg.length > 0 && (
              <span data-testid="nav-cart-count"
                className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#D94E28] text-[10px] font-bold flex items-center justify-center">
                {pkg.length}
              </span>
            )}
          </Link>

          {user ? (
            <div className="flex items-center gap-2">
              <Link to="/perfil" data-testid="nav-profile" className="flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-md border border-white/25 pl-1 pr-3 py-1 text-white text-sm">
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
                ) : (
                  <span className="h-7 w-7 rounded-full bg-[#6E7E56] flex items-center justify-center text-xs">{user.name?.[0]}</span>
                )}
                <span className="hidden md:inline max-w-[90px] truncate">{user.name}</span>
              </Link>
              <button onClick={logout} data-testid="logout-btn" className="text-white/80 hover:text-white p-2">
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={login}
              data-testid="start-btn"
              className="hidden lg:block rounded-full bg-[#16221D] text-white text-sm font-semibold px-6 py-2.5 hover:bg-[#D94E28] transition-colors duration-300"
            >
              {t.nav.start}
            </button>
          )}

          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" className="lg:hidden text-white p-2">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden mx-4 mb-4 rounded-2xl bg-[#16221D]/95 backdrop-blur-xl border border-white/15 p-3" data-testid="mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className="block px-4 py-3 text-white/90 rounded-xl hover:bg-white/10">
              {l.label}
            </Link>
          ))}
          <Link to="/gestionar-reserva" onClick={() => setOpen(false)} data-testid="mobile-nav-manage"
            className="flex items-center gap-2 px-4 py-3 text-white/90 rounded-xl hover:bg-white/10">
            <Ticket className="h-4 w-4" />{lang === "es" ? "Gestionar mi reserva" : "Manage my booking"}
          </Link>
          <Link to="/perfil" onClick={() => setOpen(false)} className="block px-4 py-3 text-white/90 rounded-xl hover:bg-white/10">{t.nav.profile}</Link>
          <button onClick={toggleLang} data-testid="mobile-lang-toggle"
            className="w-full text-left px-4 py-3 text-white rounded-xl hover:bg-white/10">
            {lang === "es" ? "🇺🇸 Switch to English" : "🇲🇽 Cambiar a español"}
          </button>
          {!user && (
            <button onClick={() => { setOpen(false); login(); }} data-testid="mobile-start-btn"
              className="mt-2 w-full rounded-xl bg-[#D94E28] text-white text-sm font-semibold px-4 py-3 hover:bg-[#B83B1A] transition-colors duration-300">
              {t.nav.start}
            </button>
          )}
        </div>
      )}
    </header>
  );
};
